import { ArrowRight, Users } from "lucide-react";
import { IMAGES } from "@/data/products";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { CTASection } from "@/components/site/CTASection";

const sections = [
  {
    title: "Our Approach",
    text: "We don't think of ourselves as selling blinds. We help you decide how your rooms should feel — brighter, softer, more private, better finished — and then we make and fit the window coverings that achieve it. That means honest advice at your kitchen table, not a hard sell.",
  },
  {
    title: "What We Believe",
    text: "That measuring is a craft, not a formality. That a quotation should be clear enough to pin to the fridge. That fitting day should end with everything working, everything tidy, and nothing left for you to sort out. And that a local business earns its reputation one window at a time.",
  },
  {
    title: "Our Service",
    text: "Free home consultations, professional measuring, made-to-measure manufacture and expert installation — with aftercare you can actually reach. From a single bathroom roller blind to shutters throughout the house, the process and the care are the same.",
  },
  {
    title: "Local Expertise",
    text: "Birmingham born and bred — we measure, make and fit right across the city and the wider West Midlands. We know local homes inside out, from Victorian bay windows in Moseley to new-build apartments in the Jewellery Quarter, and we bring honest, local advice to every visit.",
  },
  {
    title: "Our Promise",
    text: "Free, friendly advice. Careful measuring. A clear, no-obligation quote. Made-to-measure products, fitted properly. And if you ever need us afterwards, you call us and speak to a person.",
  },
];

export default function About() {
  const { openForm } = useLeadForm();
  return (
    <>
      <SEO
        title="About My Blinds Company | Birmingham Blinds & Shutters Specialists"
        description="My Blinds Company is a Birmingham specialist in made-to-measure blinds and shutters — free home consultations, professional measuring and expert fitting across the local area."
        path="/about"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "About Us", path: "/about" },
          ]),
        ]}
      />
      <section className="bg-warm-50 grain py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="justify-center">About Us</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-slate-900 tracking-tight leading-tight">
              A Birmingham business built around your windows
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
              My Blinds Company specialises in made-to-measure blinds and
              shutters for homes and businesses across Birmingham. Everything
              we fit is measured by us, made for the individual window, and
              installed by people who take pride in the finish.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {sections.map((s, i) => (
            <Reveal key={s.title}>
              <div
                className={`grid lg:grid-cols-2 gap-10 items-center ${
                  i % 2 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <h2 className="font-serif text-3xl text-slate-900 tracking-tight">{s.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">{s.text}</p>
                </div>
                <div className="overflow-hidden rounded-[2rem] aspect-[16/9] shadow-lg">
                  <img
                    src={[IMAGES.livingRoom, IMAGES.bedroom, IMAGES.kitchen, IMAGES.bayWindow, IMAGES.shutters][i]}
                    alt={`${s.title} — My Blinds Company, Birmingham`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="team-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-navy-900">
              <Users className="h-6 w-6 text-white" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Meet the Team
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              The people behind your home visits and fittings will be
              introduced here soon — real photos and real names, because we
              think you should know who's coming to your door.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl text-slate-900 tracking-tight">
            See the difference for yourself
          </h2>
          <button
            type="button"
            data-testid="about-book-btn"
            onClick={() => openForm({ source: "about_page" })}
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy-900 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
          >
            Book a Free Consultation <ArrowRight className="h-4 w-4" />
          </button>
        </Reveal>
      </section>

      <CTASection source="about_final_cta" />
    </>
  );
}
